import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {AuthService} from '../../shared/auth.service';
import {UserStatus} from '../../interfaces/user';

export type CompanyTab = 'profile' | 'billing' | 'vat' | 'kilometres' | 'reimbursement' | 'quotes';
export type CompanySection = 'billing' | 'vat';

export const COMPANY_TABS: CompanyTab[] = ['profile', 'billing', 'vat', 'kilometres', 'reimbursement', 'quotes'];
export const COMPANY_TAB_ICONS: Record<CompanyTab, string> = {
  profile: 'home-outline', billing: 'credit-card-outline', vat: 'percent-outline', kilometres: 'car-outline', reimbursement: 'person-outline', quotes: 'file-text-outline',
};

// Each field of the company billing record lives on one tab only. Billing name and KvK sit with
// the address and IBAN: together they are the invoice header. The readiness keys are
// `company.<field>` (InvoicingReadiness::company), so this is also their owner.
export const SECTION_FIELDS: Record<CompanySection, string[]> = {
  billing: ['name', 'kvk', 'street', 'postcode', 'town', 'country', 'iban', 'peppol_id', 'invoicing_starts_on'],
  vat: ['vat_id'],
};

// The form's own label per field, so the setup card names a field as the tab shows it.
export const FIELD_LABELS: Record<string, string> = {
  name: 'invoicing.name', kvk: 'invoicing.kvk', street: 'invoicing.street', postcode: 'invoicing.postcode',
  town: 'invoicing.town', country: 'invoicing.country', iban: 'invoicing.company.iban', peppol_id: 'invoicing.peppol_id',
  invoicing_starts_on: 'invoicing.company.starts_on', vat_id: 'invoicing.company.vat_id',
};

export const companyFieldOf = (key: string): string => key.replace(/^company\./, '');

export function companyTabOf(key: string): CompanySection {
  const field = companyFieldOf(key);
  return (Object.keys(SECTION_FIELDS) as CompanySection[]).find(s => SECTION_FIELDS[s].includes(field));
}

export function groupMissing(keys: string[]): Record<CompanyTab, string[]> {
  const groups = Object.fromEntries(COMPANY_TABS.map(t => [t, []])) as Record<CompanyTab, string[]>;
  keys.filter(k => k.startsWith('company.')).forEach(k => companyTabOf(k) && groups[companyTabOf(k)].push(k));
  return groups;
}

// Same gates as the server (#1515, #1516, #1697): an active Admin of this tenant, with the feature on.
export function companyTabAllowed(tab: CompanyTab, auth: AuthService): boolean {
  const me = auth.user.getValue();
  const active = auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE;
  const feature = tab === 'reimbursement' ? me?.trips_enabled : tab === 'quotes' ? me?.quotes_enabled : me?.invoicing_enabled;
  return tab === 'profile' || (active && !!feature);
}

/** Readiness and focus shared by the My company shell and its routed tabs. */
@Injectable({providedIn: 'root'})
export class CompanyTabsService {
  readonly missing$ = new BehaviorSubject<string[] | null>(null);
  readonly focus$ = new BehaviorSubject<string | null>(null);
  shellDirty: () => boolean = () => false;
  // Codex I1: the last save in flight. A tab mounting while it saves awaits this first,
  // so its own load can't read the pre-save record and then overwrite the save with it.
  pending: Promise<unknown> = Promise.resolve();

  reset() {
    this.missing$.next(null);
    this.focus$.next(null);
    this.shellDirty = () => false;
    this.pending = Promise.resolve();
  }
}
